import { CaretDown, CaretUp, ChevronRight } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import {
  downloadSalesforceApexClassesReport,
  getSalesforceApexClasses
} from '../../api/salesforceAudit/salesforceAudit.service'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import Table from '../../components/common/DDS/Table/Table'
import Tabs from '../../components/common/DDS/Tabs/Tabs'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { paginate } from '../../utils/CommonUtils'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useStoreContext } from '../../store/StoreContext'

const SFApexClassesOverview: React.FC = () => {
  const store = useStoreContext()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [tabIndex, setTabIndex] = React.useState<string>(
    searchParams.get('tab') ?? '1'
  )
  const { salesforceAuditStore } = store
  const [currentPageActive, setCurrentPageActive] = useState(1)
  const [currentPageInactive, setCurrentPageInactive] = useState(1)

  const [apexClassesData, setApexClassesData] = useState<any[]>([])

  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId

  useEffect(() => {
    const fetchApexClasses = async () => {
      if (!salesforceAuditId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await getSalesforceApexClasses({
          id: salesforceAuditId
        })

        const apexClasses = response.data.map((item: any) => item.record)
        salesforceAuditStore.setApexClassesData(response?.data)

        setApexClassesData(apexClasses) // set single profile or change logic for multiple
      } catch (err: any) {
        console.error('Error fetching profiles:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchApexClasses()
  }, [salesforceAuditId])

  const activeData = apexClassesData?.filter(
    (item: any) => item.Status === 'Active'
  )
  const inactiveData = apexClassesData?.filter(
    (item: any) => item.Status === 'Inactive'
  )

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const activeFilteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm.trim().toLowerCase()
    if (!trimmedSearch) return activeData

    return activeData.filter((item: any) => {
      const name = item?.Name?.toLowerCase() || ''
      const modifiedBy = item?.LastModifiedBy?.Name?.toLowerCase() || ''
      const author = item?.CreatedBy?.Name?.toLowerCase() || ''

      const apiVersion = String(item?.ApiVersion || '').toLowerCase()
      const length = String(item?.LengthWithoutComments || '').toLowerCase()

      let formattedDates: string[] = []
      if (item?.LastModifiedDate) {
        const m = moment(item.LastModifiedDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('D').toLowerCase(),
          m.format('MMMM').toLowerCase()
        ]
      }

      const isDateMatch = formattedDates.some((d) => d.includes(trimmedSearch))

      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'LastModifiedBy':
          return modifiedBy.includes(trimmedSearch)
        case 'CreatedBy':
          return author.includes(trimmedSearch)
        case 'LastModifiedDate':
          return isDateMatch
        case 'ApiVersion':
          return apiVersion.includes(trimmedSearch)
        case 'LengthWithoutComments':
          return length.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            modifiedBy.includes(trimmedSearch) ||
            author.includes(trimmedSearch) ||
            isDateMatch ||
            apiVersion.includes(trimmedSearch) ||
            length.includes(trimmedSearch)
          )
      }
    })
  }, [activeData, activeSearchTerm, searchColumn])

  const activeSortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return activeFilteredData

    return [...activeFilteredData].sort((a: any, b: any) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1

      if (sortConfig.key === 'Name') {
        const aName = a?.Name?.toLowerCase() || ''
        const bName = b?.Name?.toLowerCase() || ''
        return aName.localeCompare(bName) * dir
      }

      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = new Date(a?.LastModifiedDate || 0).getTime()
        const dateB = new Date(b?.LastModifiedDate || 0).getTime()
        return (dateA - dateB) * dir
      }

      if (sortConfig.key === 'LastModifiedBy') {
        const aMod = a?.LastModifiedBy?.Name?.toLowerCase() || ''
        const bMod = b?.LastModifiedBy?.Name?.toLowerCase() || ''
        return aMod.localeCompare(bMod) * dir
      }

      if (sortConfig.key === 'CreatedBy') {
        const aAuthor = a?.CreatedBy?.Name?.toLowerCase() || ''
        const bAuthor = b?.CreatedBy?.Name?.toLowerCase() || ''
        return aAuthor.localeCompare(bAuthor) * dir
      }

      if (sortConfig.key === 'ApiVersion') {
        const aVer = Number(a?.ApiVersion || 0)
        const bVer = Number(b?.ApiVersion || 0)
        return (aVer - bVer) * dir
      }

      if (sortConfig.key === 'LengthWithoutComments') {
        const aLen = Number(a?.LengthWithoutComments || 0)
        const bLen = Number(b?.LengthWithoutComments || 0)
        return (aLen - bLen) * dir
      }

      // fallback: string comparison
      const aVal = a?.[sortConfig.key!] || ''
      const bVal = b?.[sortConfig.key!] || ''

      return String(aVal).localeCompare(String(bVal)) * dir
    })
  }, [activeFilteredData, sortConfig, salesforceAuditStore])

  const inActiveFilteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm.trim().toLowerCase()
    if (!trimmedSearch) return inactiveData

    return activeData.filter((item: any) => {
      const name = item?.Name?.toLowerCase() || ''
      const modifiedBy = item?.LastModifiedBy?.Name?.toLowerCase() || ''
      const author = item?.CreatedBy?.Name?.toLowerCase() || ''

      const apiVersion = String(item?.ApiVersion || '').toLowerCase()
      const length = String(item?.LengthWithoutComments || '').toLowerCase()

      let formattedDates: string[] = []
      if (item?.LastModifiedDate) {
        const m = moment(item.LastModifiedDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('D').toLowerCase(),
          m.format('MMMM').toLowerCase()
        ]
      }

      const isDateMatch = formattedDates.some((d) => d.includes(trimmedSearch))

      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'LastModifiedBy':
          return modifiedBy.includes(trimmedSearch)
        case 'CreatedBy':
          return author.includes(trimmedSearch)
        case 'LastModifiedDate':
          return isDateMatch
        case 'ApiVersion':
          return apiVersion.includes(trimmedSearch)
        case 'LengthWithoutComments':
          return length.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            modifiedBy.includes(trimmedSearch) ||
            author.includes(trimmedSearch) ||
            isDateMatch ||
            apiVersion.includes(trimmedSearch) ||
            length.includes(trimmedSearch)
          )
      }
    })
  }, [activeData, activeSearchTerm, searchColumn, salesforceAuditStore])

  const inActiveSortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return inActiveFilteredData

    return [...inActiveFilteredData].sort((a: any, b: any) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1

      if (sortConfig.key === 'Name') {
        const aName = a?.Name?.toLowerCase() || ''
        const bName = b?.Name?.toLowerCase() || ''
        return aName.localeCompare(bName) * dir
      }

      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = new Date(a?.LastModifiedDate || 0).getTime()
        const dateB = new Date(b?.LastModifiedDate || 0).getTime()
        return (dateA - dateB) * dir
      }

      if (sortConfig.key === 'LastModifiedBy') {
        const aMod = a?.LastModifiedBy?.Name?.toLowerCase() || ''
        const bMod = b?.LastModifiedBy?.Name?.toLowerCase() || ''
        return aMod.localeCompare(bMod) * dir
      }

      if (sortConfig.key === 'CreatedBy') {
        const aAuthor = a?.CreatedBy?.Name?.toLowerCase() || ''
        const bAuthor = b?.CreatedBy?.Name?.toLowerCase() || ''
        return aAuthor.localeCompare(bAuthor) * dir
      }

      if (sortConfig.key === 'ApiVersion') {
        const aVer = Number(a?.ApiVersion || 0)
        const bVer = Number(b?.ApiVersion || 0)
        return (aVer - bVer) * dir
      }

      if (sortConfig.key === 'LengthWithoutComments') {
        const aLen = Number(a?.LengthWithoutComments || 0)
        const bLen = Number(b?.LengthWithoutComments || 0)
        return (aLen - bLen) * dir
      }

      // fallback: string comparison
      const aVal = a?.[sortConfig.key!] || ''
      const bVal = b?.[sortConfig.key!] || ''

      return String(aVal).localeCompare(String(bVal)) * dir
    })
  }, [inActiveFilteredData, sortConfig, salesforceAuditStore])

  useEffect(() => {
    const organizationId = store.scopeStore.getScope()[
      'organization-id'
    ] as string
    const projectId = store.scopeStore.getScope()['project-id'] as string
    const environmentId = store.scopeStore.getScope()[
      'environment-id'
    ] as string

    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: `${replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          organizationId,
          projectId,
          environmentId
        )}?page=1&tab=1`
      },
      {
        label: (
          <div
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            className='cursor-not-allowed'
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              className='pointer-events-none'
            >
              Apex Classes
            </div>
          </div>
        ),
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/apex-classes',
          organizationId,
          projectId,
          environmentId
        )
      },
      {
        label: tabIndex === '1' ? 'Active' : 'Inactive',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/apex-classes',
          organizationId,
          projectId,
          environmentId
        )
      }
    ])
  }, [tabIndex])

  const downloadApexClasses = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string,
      isActive: tabIndex === '1' ? true : false
    }

    try {
      const response = await downloadSalesforceApexClassesReport(payload)
      const orgTZ = store.userStore.getOrganizationPreference().timeZone
      const apexClasses: any[] = response.data || []

      const mappedClasses = apexClasses.map((cls) => {
        return {
          Name: cls?.name || '-',
          ApiVersion: cls?.apiVersion || '-',
          Status: cls?.status || '-',
          CreatedBy: cls?.createdBy?.Name || '-',
          LastModifiedBy: cls?.lastModifiedBy?.Name || '-',
          CreatedDate:
            cls?.createdDate && cls?.createdDate !== '-'
              ? timeZone(
                  orgTZ,
                  cls?.createdDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          LastModifiedDate:
            cls?.lastModifiedDate && cls?.lastModifiedDate !== '-'
              ? timeZone(
                  orgTZ,
                  cls?.lastModifiedDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          length: cls?.lengthWithoutComments || '-'
        }
      })

      const date = new Date()
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(
          2,
          '0'
        )}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`

      convertJsonToCsvDownload(
        mappedClasses,
        `Salesforce_ApexClasses_Report_${formattedDate}.csv`
      )

      ToastNotification({
        type: 'success',
        message: `Successfully exported ${mappedClasses.length} Apex class(es)`
      })
    } catch (error: any) {
      ToastNotification({
        type: 'error',
        message:
          typeof error === 'string'
            ? error
            : error?.message || 'Error downloading Apex classes report'
      })
    } finally {
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  const activeColumns = [
    {
      columnHeaderStyle: { width: '180px' },
      columnDataStyle: { width: '180px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Class Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[180px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 18 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[180px] max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => {
        return (
          <div className='flex justify-start text-start row items-start'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              {text
                ? moment(text).format(timeFormats.shortMonthDateYearTime)
                : '-'}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Modified by
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),

      dataLabel: 'LastModifiedBy',
      render: (record: any) => {
        const lastModifiedByName = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${lastModifiedByName}`}
              label={lastModifiedByName}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof lastModifiedByName === 'string' &&
                lastModifiedByName.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {lastModifiedByName}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Author
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('CreatedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'CreatedBy',
      render: (record: any) => {
        const createdBy = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${createdBy}`}
              label={createdBy}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof createdBy === 'string' && createdBy.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {createdBy}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              API Version
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('ApiVersion')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'ApiVersion' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'ApiVersion' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'ApiVersion',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text || '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Length
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LengthWithoutComments')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LengthWithoutComments' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LengthWithoutComments' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LengthWithoutComments',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text || '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(`${text}?tab=${tabIndex}`)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]

  const inActiveColumns = [
    {
      columnHeaderStyle: { width: '180px' },
      columnDataStyle: { width: '180px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Class Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[180px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 18 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => {
        return (
          <div className='flex justify-start text-start row items-start'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              {text
                ? moment(text).format(timeFormats.shortMonthDateYearTime)
                : '-'}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Modified by
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedBy',
      render: (record: any) => {
        const lastModifiedByName = record?.Name || '-'
        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${lastModifiedByName}`}
              label={lastModifiedByName}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof lastModifiedByName === 'string' &&
                lastModifiedByName.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {lastModifiedByName}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Author
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('CreatedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'CreatedBy',
      render: (record: any) => {
        const createdBy = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${createdBy}`}
              label={createdBy}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof createdBy === 'string' && createdBy.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {createdBy}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              API Version
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('ApiVersion')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'ApiVersion' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'ApiVersion' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'ApiVersion',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text || '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Length
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LengthWithoutComments')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LengthWithoutComments' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LengthWithoutComments' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LengthWithoutComments',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text || '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },

    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(`${text}?tab=${tabIndex}`)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]

  const tabs = [
    {
      id: '1',
      label:
        tabIndex === '1' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            Active ({activeData.length})
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            Active ({activeData.length})
          </DDSTypography.Title>
        ),
      children: (
        <div className='flex flex-col py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())

                setCurrentPageActive(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPageActive(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'Class Name', value: 'Name' },
                  { label: 'Last Modified Date', value: 'LastModifiedDate' },
                  { label: 'Modified by', value: 'LastModifiedBy' },
                  { label: 'Author', value: 'CreatedBy' },
                  { label: 'API Version', value: 'ApiVersion' },
                  { label: 'Length', value: 'LengthWithoutComments' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPageActive(1)
                      setActiveSearchTerm(searchTerm)
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {activeSortedData?.length > 0 ? (
            <Table
              columns={activeColumns}
              data={paginate(
                activeSortedData,
                currentPageActive,
                SalesforceAuditStore.pageSize
              )}
              totalCount={activeSortedData?.length}
              currentPage={currentPageActive}
              rowsPerPage={SalesforceAuditStore.pageSize}
              onPageChange={(page) => setCurrentPageActive(page)}
              clickable={(id) => {
                navigate(`${id}?tab=${tabIndex}`)
              }}
              clickType='SalesforceAudit'
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Apex Classes match your search.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Apex Classes have been created yet</div>}
            />
          )}
        </div>
      )
    },
    {
      id: '2',
      label:
        tabIndex === '2' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            Inactive ({inactiveData.length})
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            Inactive ({inactiveData.length})
          </DDSTypography.Title>
        ),
      children: (
        <div className='flex flex-col py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())

                setCurrentPageActive(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPageActive(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'Class Name', value: 'Name' },
                  { label: 'Last Modified Date', value: 'LastModifiedDate' },
                  { label: 'Modified by', value: 'LastModifiedBy' },
                  { label: 'Author', value: 'CreatedBy' },
                  { label: 'API Version', value: 'ApiVersion' },
                  { label: 'Length', value: 'LengthWithoutComments' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPageActive(1)
                      setActiveSearchTerm(searchTerm)
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {inActiveSortedData?.length > 0 ? (
            <Table
              columns={inActiveColumns}
              data={paginate(
                inActiveSortedData,
                currentPageInactive,
                SalesforceAuditStore.pageSize
              )}
              totalCount={inActiveSortedData?.length}
              currentPage={currentPageInactive}
              rowsPerPage={SalesforceAuditStore.pageSize}
              onPageChange={(page) => setCurrentPageInactive(page)}
              clickable={(id) => {
                navigate(`${id}?tab=${tabIndex}`)
              }}
              clickType='SalesforceAudit'
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Apex Classes match your search.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Apex Classes have been created yet</div>}
            />
          )}
        </div>
      )
    }
  ]

  return (
    <>
      {loading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='flex flex-col w-full py-[24px]'>
          <Tabs
            tabOptionsStyle={{
              minWidth: 'fit-content',
              width: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 12px',
              marginRight: '8px'
            }}
            tabOptions={tabs}
            id='tabs-grouped-api'
            onChange={(index: any) => {
              setTabIndex(index.toString())
              setSearchParams({
                ...searchParams,
                tab: index.toString()
              })
            }}
            exportOption={true}
            showActionButton={true}
            onActionButtonPress={downloadApexClasses}
            actionButtonLabel='Export'
            actionButtonDisabled={
              store.uiStore.getGlobalLoader() ||
              (tabIndex === '1' && activeSortedData.length === 0) ||
              (tabIndex === '2' && inActiveSortedData?.length === 0)
            }
            activeTab={tabIndex}
          />
        </div>
      )}
    </>
  )
}

export default observer(SFApexClassesOverview)
